import type { MediaAsset } from "@lucky-try/contracts";
import { TrashIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { mediaQuery } from "../lib/queries";
import { useCan } from "../lib/session";
import { Dialog } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/form";
import { EmptyState, Loading } from "./ui/misc";

export function useMediaUpload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return api<MediaAsset>("/admin/media/upload", { form });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["media"] }),
    onError: (error) => toast.error(errorMessage(error)),
  });
}

/** Shared asset browser: used both by the media library and by the game editor pickers. */
export function MediaPicker({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (asset: MediaAsset) => void;
}) {
  const [term, setTerm] = useState("");
  const canUpload = useCan("media.write");
  const upload = useMediaUpload();
  const fileInput = useRef<HTMLInputElement>(null);
  const media = useQuery({ ...mediaQuery({ page: 1, q: term || undefined }), enabled: open });

  return (
    <Dialog open={open} onClose={onClose} title="Select image" width="lg">
      <div className="mb-3 flex items-center gap-2">
        <Input
          placeholder="Search by file name or alt text"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
        />
        {canUpload && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) upload.mutate(file, { onSuccess: (asset) => onSelect(asset) });
              }}
            />
            <Button disabled={upload.isPending} onClick={() => fileInput.current?.click()}>
              <UploadSimpleIcon /> {upload.isPending ? "Uploading…" : "Upload"}
            </Button>
          </>
        )}
      </div>
      {media.isPending ? (
        <Loading />
      ) : media.data && media.data.items.length > 0 ? (
        <div className="grid max-h-[55vh] grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-6">
          {media.data.items.map((asset) => (
            <button
              key={asset.id}
              type="button"
              onClick={() => onSelect(asset)}
              title={asset.originalFilename ?? asset.url}
              className="border-line bg-raised hover:border-accent aspect-square overflow-hidden rounded border"
            >
              <img
                src={asset.url}
                alt={asset.alt ?? ""}
                loading="lazy"
                className="size-full object-cover"
              />
            </button>
          ))}
        </div>
      ) : (
        <EmptyState>No images yet.</EmptyState>
      )}
    </Dialog>
  );
}

export function ImagePickerField({
  label,
  hint,
  asset,
  onChange,
}: {
  label: string;
  hint?: string;
  asset: MediaAsset | null;
  onChange: (asset: MediaAsset | null) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <span className="text-body text-caption font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="border-line bg-raised text-muted hover:border-accent text-caption grid size-16 shrink-0 place-items-center overflow-hidden rounded border"
        >
          {asset ? <img src={asset.url} alt="" className="size-full object-cover" /> : "Choose"}
        </button>
        <div className="flex flex-col gap-1">
          <Button size="sm" onClick={() => setOpen(true)}>
            {asset ? "Replace" : "Select image"}
          </Button>
          {asset && (
            <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
              <TrashIcon /> Clear
            </Button>
          )}
        </div>
      </div>
      {hint && <span className="text-muted text-caption">{hint}</span>}
      <MediaPicker
        open={open}
        onClose={() => setOpen(false)}
        onSelect={(selected) => {
          onChange(selected);
          setOpen(false);
        }}
      />
    </div>
  );
}
